import {useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import aiIconUrl from '../../../assets/icons/ai.svg';
import {relativeTime} from '../utils/relativeTime';
import {useAiSummaryCollapse} from '../hooks/useAiSummaryCollapse';
import {useRegenerateAiSummary} from '../hooks/useRegenerateAiSummary';
import {useSubmitAiSummaryFeedback} from '../hooks/useSubmitAiSummaryFeedback';
import type {AiSummaryBlock_ticket$key} from './__generated__/AiSummaryBlock_ticket.graphql';

const AiSummaryFragment = graphql`
	fragment AiSummaryBlock_ticket on Ticket {
		id
		aiSummaryState
		aiSummary {
			summary
			statusSignal
			sentiment
			commentCount
			emailCount
			generatedAt
			modelVersion
			feedbackScore
			feedbackText
			isFinal
			staleness {
				isStale
				newComments
				newEmails
				newStatusEvents
				total
			}
		}
	}
`;

const Section = styled.section`
	padding: 20px;
	background: #f3e8ff;
	border: 1px solid #e9d5ff;
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
`;
const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
`;
const Left = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const Right = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const Icon = styled.img`
	width: 16px;
	height: 16px;
	color: ${p => p.theme.sys.color.brand.primary['1']};
`;
const Title = styled.span`
	${p => p.theme.sys.font?.subheading?.['2']}
	color: ${p => p.theme.sys.color.brand.primary['1']};
`;
const AiPill = styled.span`
	padding: 2px 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	background: #e9d5ff;
	color: ${p => p.theme.sys.color.brand.primary['1']};
	border-radius: 9999px;
	border: 1px solid ${p => p.theme.sys.color.background.accent['1']};
`;
const Timestamp = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const Body = styled.p`
	margin: 12px 0 0;
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	white-space: pre-wrap;
`;
const Provenance = styled.div`
	margin-top: 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const LoadingRow = styled.div`
	margin-top: 12px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const StalenessPill = styled.span`
	padding: 2px 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: ${p => p.theme.sys.color.brand.primary['1']};
	background: #e9d5ff;
	border-radius: 9999px;
	cursor: default;
`;
const ErrorRow = styled.div`
	margin-top: 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: ${p => p.theme.sys.color.text.semantic.error};
`;
const FeedbackNote = styled.div`
	margin-top: 8px;
	display: flex;
	flex-direction: column;
	gap: 6px;
`;
const NoteInput = styled.textarea`
	${p => p.theme.sys.font?.detail?.['1']}
	color: ${p => p.theme.sys.color.text.content.main};
	border: 1px solid #e9d5ff;
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	padding: 6px 8px;
	resize: vertical;
	min-height: 48px;
`;
const NoteActions = styled.div`
	display: flex;
	gap: 8px;
`;
const Placeholder = styled.div`
	margin-top: 12px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;
const FinalLabel = styled.span`
	padding: 2px 8px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: ${p => p.theme.sys.color.brand.primary['1']};
	background: #e9d5ff;
	border-radius: 9999px;
`;

function pluralise(count: number, noun: string): string {
	return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

interface Props {
	ticketRef: AiSummaryBlock_ticket$key;
	/** True while a generation job is in flight and no summary exists yet. */
	generating?: boolean;
}

export function AiSummaryBlock({ticketRef, generating = false}: Props) {
	const ticket = useFragment<AiSummaryBlock_ticket$key>(AiSummaryFragment, ticketRef);
	const {collapsed, toggle} = useAiSummaryCollapse(ticket.id);
	const {regenerate, inFlight, error} = useRegenerateAiSummary();
	const {submit: submitFeedback, inFlight: feedbackInFlight} = useSubmitAiSummaryFeedback();
	const summary = ticket.aiSummary;
	const state = ticket.aiSummaryState;
	const isFinal = Boolean(summary?.isFinal);
	const busy = generating || inFlight;

	const score = summary?.feedbackScore ?? null;
	const [showNote, setShowNote] = useState(false);
	const [note, setNote] = useState(summary?.feedbackText ?? '');

	const vote = (next: 1 | -1) => {
		if (score === next) {
			setShowNote(false);
			submitFeedback({ticketId: ticket.id, score: null, feedbackText: null});
			return;
		}
		if (next === -1) {
			setShowNote(true);
			submitFeedback({ticketId: ticket.id, score: -1, feedbackText: summary?.feedbackText ?? null});
		} else {
			setShowNote(false);
			submitFeedback({ticketId: ticket.id, score: 1, feedbackText: null});
		}
	};

	if (!summary && state === 'NO_ACTIVITY') {
		return (
			<Section>
				<Left>
					<Icon src={aiIconUrl} alt="" role="img" />
					<Title>AI summary</Title>
					<AiPill>AI</AiPill>
				</Left>
				<Placeholder>AI summary will appear once the ticket has activity.</Placeholder>
			</Section>
		);
	}

	if (!summary && state === 'FAILED') {
		return (
			<Section>
				<Left>
					<Icon src={aiIconUrl} alt="" role="img" />
					<Title>AI summary</Title>
					<AiPill>AI</AiPill>
				</Left>
				<Placeholder>The AI summary couldn’t be generated. You can try again.</Placeholder>
				<Button emphasis={EMPHASIS.LOW} disabled={busy} onPress={() => regenerate(ticket.id)}>
					{inFlight ? 'Trying…' : 'Try again'}
				</Button>
				{error && <ErrorRow>{error.message}</ErrorRow>}
			</Section>
		);
	}

	if (!summary && (busy || state === 'PENDING')) {
		return (
			<Section>
				<Left>
					<Icon src={aiIconUrl} alt="" role="img" />
					<Title>AI summary</Title>
					<AiPill>AI</AiPill>
				</Left>
				<LoadingRow>{inFlight ? 'Regenerating…' : 'Generating summary…'}</LoadingRow>
			</Section>
		);
	}

	if (!summary) return null;

	const provenance = `${pluralise(summary.commentCount, 'comment')} · ${pluralise(summary.emailCount, 'email')}`;
	const staleness = summary.staleness;
	const stalenessTitle = staleness
		? `${pluralise(staleness.newComments, 'comment')}, ${pluralise(staleness.newEmails, 'email')}, ${pluralise(
				staleness.newStatusEvents,
				'status change'
		  )}`
		: undefined;

	return (
		<Section>
			<Header>
				<LabelledButton emphasis={EMPHASIS.LOW} onPress={toggle} aria-expanded={!collapsed} label="AI summary">
					<Left>
						<Icon src={aiIconUrl} alt="" role="img" />
						<Title>AI summary</Title>
						<AiPill>AI</AiPill>
					</Left>
				</LabelledButton>
				<Right>
					{!isFinal && staleness?.isStale && staleness.total > 0 && (
						<StalenessPill title={stalenessTitle}>
							{pluralise(staleness.total, 'new item')} since last summary — regenerate?
						</StalenessPill>
					)}
					{isFinal && <FinalLabel>Final summary</FinalLabel>}
					<Timestamp>{relativeTime(summary.generatedAt)}</Timestamp>
					<LabelledButton
						emphasis={EMPHASIS.MEDIUM}
						label="Thumbs up"
						aria-pressed={score === 1}
						disabled={feedbackInFlight}
						onPress={() => vote(1)}
					>
						👍
					</LabelledButton>
					<LabelledButton
						emphasis={EMPHASIS.MEDIUM}
						label="Thumbs down"
						aria-pressed={score === -1}
						disabled={feedbackInFlight}
						onPress={() => vote(-1)}
					>
						👎
					</LabelledButton>
					{!isFinal && (
						<Button emphasis={EMPHASIS.LOW} disabled={busy} onPress={() => regenerate(ticket.id)}>
							{inFlight ? 'Regenerating…' : 'Regenerate'}
						</Button>
					)}
				</Right>
			</Header>
			{!collapsed && (
				<>
					<Body>{summary.summary}</Body>
					<Provenance>{provenance}</Provenance>
				</>
			)}
			{showNote && !collapsed && (
				<FeedbackNote>
					<NoteInput
						value={note}
						placeholder="What was wrong? (optional)"
						onChange={e => setNote(e.target.value)}
						aria-label="What was wrong?"
					/>
					<NoteActions>
						<Button
							emphasis={EMPHASIS.LOW}
							disabled={feedbackInFlight}
							onPress={() => {
								submitFeedback({ticketId: ticket.id, score: -1, feedbackText: note.trim() || null});
								setShowNote(false);
							}}
						>
							Save note
						</Button>
					</NoteActions>
				</FeedbackNote>
			)}
			{error && <ErrorRow>{error.message}</ErrorRow>}
		</Section>
	);
}
